import { useSearchParams } from "react-router";

import { Stack, Typography } from "@mui/material";
import { EMPTY_VALUE_PLACEHOLDER } from "@shared";
import { useQuery } from "@tanstack/react-query";

import { VolunteerSearchResult, volunteersApi } from "@/api";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { getRoleLabelKey } from "@/common/components/role-chip/RoleChip.utils";
import { createColumns } from "@/common/components/table/DataTable.utils";
import { UserAvatar } from "@/common/components/user-avatar/UserAvatar";
import { UserPermission } from "@/common/contexts/current-user-context/CurrentUserContextProvider.types";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

import { formatVolunteerListName } from "../Volunteers.utils";

const SEARCH_PARAM = "searchText";

export function useVolunteers() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchText = searchParams.get(SEARCH_PARAM) ?? undefined;

  const { data, isLoading } = useQuery({
    queryKey: [QueryKey.Volunteers, searchText],
    queryFn: () => volunteersApi.searchVolunteers({ searchText }),
    meta: { loadingScope: "pageContent" },
  });

  const columns = createColumns<VolunteerSearchResult>([
    {
      field: "lastName",
      headerName: t("volunteers.table.volunteer"),
      flex: 2,
      renderCell: (params) => {
        const row = params.row as VolunteerSearchResult;
        return (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: "100%" }}>
            <UserAvatar label={row.username} role={UserPermission.Volunteer} />
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600}>
                {formatVolunteerListName(row)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(getRoleLabelKey(UserPermission.Volunteer))}
              </Typography>
            </Stack>
          </Stack>
        );
      },
    },
    {
      field: "username",
      headerName: t("volunteers.table.kzz"),
      flex: 1,
      renderCell: (params) => <EllipsisText value={params.value as string} variant="body2" fontWeight={600} />,
    },
    {
      field: "phone",
      headerName: t("volunteers.table.phone"),
      flex: 1,
      renderCell: (params) => (
        <EllipsisText value={(params.value as string | null) ?? EMPTY_VALUE_PLACEHOLDER} variant="body2" />
      ),
    },
    {
      field: "email",
      headerName: t("volunteers.table.email"),
      flex: 1.5,
      renderCell: (params) => (
        <EllipsisText value={(params.value as string | null) ?? EMPTY_VALUE_PLACEHOLDER} variant="body2" />
      ),
    },
  ]);

  function setSearchText(value: string | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(SEARCH_PARAM, value);
      } else {
        next.delete(SEARCH_PARAM);
      }
      return next;
    });
  }

  return {
    volunteers: data?.data ?? [],
    hasAdditionalVolunteers: data?.hasMoreItems ?? false,
    isLoading,
    columns,
    searchText: searchText ?? "",
    setSearchText,
  };
}
